import type {Character, VowelMark} from './types';

/**
 * 生データ。転写は結合分音記号（NFD）で書かれているが、エクスポート時に NFC へ正規化する。
 * こうしないと「コメントは合成済み・データは結合形」という不一致が残り、
 * あとから合成済み文字で 1 件足したときに、見た目が同じで === が false になる文字が混入する。
 */
interface RawCharacter {
  char: string;
  romanized: string;
}

const rawVowels: RawCharacter[] = [
  {char: 'अ', romanized: 'a'}, // अ
  {char: 'आ', romanized: 'ā'}, // आ (ā)
  {char: 'इ', romanized: 'i'}, // इ
  {char: 'ई', romanized: 'ī'}, // ई (ī)
  {char: 'उ', romanized: 'u'}, // उ
  {char: 'ऊ', romanized: 'ū'}, // ऊ (ū)
  {char: 'ऋ', romanized: 'ṛ'}, // ऋ (ṛ)
  {char: 'ॠ', romanized: 'ṝ'}, // ॠ (ṝ)
  {char: 'ऌ', romanized: 'ḷ'}, // ऌ (ḷ)
  {char: 'ॡ', romanized: 'ḹ'}, // ॡ (ḹ)
  {char: 'ए', romanized: 'e'}, // ए
  {char: 'ऐ', romanized: 'ai'}, // ऐ
  {char: 'ओ', romanized: 'o'}, // ओ
  {char: 'औ', romanized: 'au'}, // औ
];

const rawConsonants: RawCharacter[] = [
  {char: 'क', romanized: 'ka'}, // क
  {char: 'ख', romanized: 'kha'}, // ख
  {char: 'ग', romanized: 'ga'}, // ग
  {char: 'घ', romanized: 'gha'}, // घ
  {char: 'ङ', romanized: 'ṅa'}, // ङ (ṅa)
  {char: 'च', romanized: 'ca'}, // च
  {char: 'छ', romanized: 'cha'}, // छ
  {char: 'ज', romanized: 'ja'}, // ज
  {char: 'झ', romanized: 'jha'}, // झ
  {char: 'ञ', romanized: 'ña'}, // ञ (ña)
  {char: 'ट', romanized: 'ṭa'}, // ट (ṭa)
  {char: 'ठ', romanized: 'ṭha'}, // ठ (ṭha)
  {char: 'ड', romanized: 'ḍa'}, // ड (ḍa)
  {char: 'ढ', romanized: 'ḍha'}, // ढ (ḍha)
  {char: 'ण', romanized: 'ṇa'}, // ण (ṇa)
  {char: 'त', romanized: 'ta'}, // त
  {char: 'थ', romanized: 'tha'}, // थ
  {char: 'द', romanized: 'da'}, // द
  {char: 'ध', romanized: 'dha'}, // ध
  {char: 'न', romanized: 'na'}, // न
  {char: 'प', romanized: 'pa'}, // प
  {char: 'फ', romanized: 'pha'}, // फ
  {char: 'ब', romanized: 'ba'}, // ब
  {char: 'भ', romanized: 'bha'}, // भ
  {char: 'म', romanized: 'ma'}, // म
  {char: 'य', romanized: 'ya'}, // य
  {char: 'र', romanized: 'ra'}, // र
  {char: 'ल', romanized: 'la'}, // ल
  {char: 'व', romanized: 'va'}, // व
  {char: 'श', romanized: 'śa'}, // श (śa)
  {char: 'ष', romanized: 'ṣa'}, // ष (ṣa)
  {char: 'स', romanized: 'sa'}, // स
  {char: 'ह', romanized: 'ha'}, // ह
];

const rawNumerals: RawCharacter[] = [
  {char: '०', romanized: '0'}, // ०
  {char: '१', romanized: '1'}, // १
  {char: '२', romanized: '2'}, // २
  {char: '३', romanized: '3'}, // ३
  {char: '४', romanized: '4'}, // ४
  {char: '५', romanized: '5'}, // ५
  {char: '६', romanized: '6'}, // ६
  {char: '७', romanized: '7'}, // ७
  {char: '८', romanized: '8'}, // ८
  {char: '९', romanized: '9'}, // ९
];

/**
 * 母音記号（マートラー）13 種。
 * ॄ ॢ ॣ は文法表には載るが実在の語にはほぼ現れない（कॢप्त がほぼ唯一の例）。
 * 表としては完全に保ちつつ rare を立て、出題対象からは外す。
 */
export const vowelMarks: VowelMark[] = [
  {mark: 'ा', base: 'ā'}, // ा (ā)
  {mark: 'ि', base: 'i'}, // ि (i)
  {mark: 'ी', base: 'ī'}, // ी (ī)
  {mark: 'ु', base: 'u'}, // ु (u)
  {mark: 'ू', base: 'ū'}, // ू (ū)
  {mark: 'ृ', base: 'ṛ'}, // ृ (ṛ)
  {mark: 'ॄ', base: 'ṝ', rare: true}, // ॄ (ṝ)
  {mark: 'ॢ', base: 'ḷ', rare: true}, // ॢ (ḷ)
  {mark: 'ॣ', base: 'ḹ', rare: true}, // ॣ (ḹ)
  {mark: 'े', base: 'e'}, // े (e)
  {mark: 'ै', base: 'ai'}, // ै (ai)
  {mark: 'ो', base: 'o'}, // ो (o)
  {mark: 'ौ', base: 'au'}, // ौ (au)
];

/** 出題に使う母音記号（rare を除いた 10 種） */
export const quizVowelMarks: VowelMark[] = vowelMarks.filter((m) => !m.rare);

function toCharacters(
  kind: string,
  raw: RawCharacter[],
  decorate?: (c: RawCharacter) => Pick<Character, 'pairKey' | 'note'>
): Character[] {
  return raw.map((c) => ({
    id: `${kind}:${c.char}`,
    char: c.char,
    romanized: c.romanized.normalize('NFC'),
    ...(decorate ? decorate(c) : {}),
  }));
}

export const characters = {
  vowels: toCharacters('vowel', rawVowels, () => ({note: '独立母音字'})),
  // 子音字は「k」ではなく「ka」。この内在母音がヴィラーマ・母音記号の存在理由になる
  consonants: toCharacters('consonant', rawConsonants, (c) => ({
    pairKey: c.char,
    note: '子音字は内在母音 a を含む',
  })),
  numerals: toCharacters('numeral', rawNumerals, () => ({note: 'デーヴァナーガリー数字'})),
  vowelMarks,
};

/**
 * 子音の転写から内在母音 a を取り除く。ka → k
 *
 * 旧実装は endsWith('a') で無条件に 1 文字落としていたため、'a' で終わらない転写を
 * あとから足すと無言で誤った綴りを作った。ここで例外にして気づけるようにする。
 */
export function consonantBase(romanized: string): string {
  if (!romanized.endsWith('a')) {
    throw new Error(`子音の転写は 'a' で終わる必要がある: ${romanized}`);
  }
  return romanized.slice(0, -1);
}

/**
 * 子音 × 母音記号の全組み合わせを作る。
 *
 * 旧実装には limit 引数があり、呼び出し側が「文字数スライダーの値」を渡して
 * 先頭 n 種の記号だけに絞っていた。そのためスライダーが 2 のときは ा と ि しか
 * 出題されず、上限が 12 に固定されていたので ौ は一度も出題されなかった。
 * スライダーは他モードと同じ「出題文字数」だけを意味するようにしたので、引数ごと廃止した。
 */
export function generateDependentVowels(
  consonants: Character[] = characters.consonants,
  marks: VowelMark[] = quizVowelMarks
): Character[] {
  const result: Character[] = [];

  for (const consonant of consonants) {
    const base = consonantBase(consonant.romanized);
    for (const mark of marks) {
      result.push({
        id: `dependent:${consonant.char}${mark.mark}`,
        char: consonant.char + mark.mark,
        romanized: (base + mark.base).normalize('NFC'),
        pairKey: consonant.char,
        note: `母音記号が内在母音 a を ${mark.base} に置き換える`,
      });
    }
  }

  return result;
}

/**
 * ヴィラーマ形。
 *
 * 33 子音すべてには付けない。サンスクリットでポーズの前（絶対語末）に立てる子音は
 * Whitney §149 で「常用は ḥ m n t k p ṭ、散発的に ṅ l ṇ」と限定されており、
 * ñ は語末に一切現れない（§143）。ḥ ṃ は記号側なので ayogavāha カテゴリが受け持つ。
 *
 * 表記は子音 + U+094D の 2 コードポイントのみ。ZWNJ (U+200C) も点線円 (U+25CC) も
 * 入れない。ヴィラーマの後に子音が続かなければシェイパーが常に可視のヴィラーマを描く
 * （Unicode Indic FAQ）。不可視文字を混ぜると NFC 正規化と文字列比較の邪魔になる。
 */
const VIRAMA = '\u094D';
const FINAL_CONSONANTS = ['क', 'ट', 'त', 'प', 'ङ', 'ण', 'न', 'म', 'ल'];

export function generateVirama(consonants: Character[] = characters.consonants): Character[] {
  return FINAL_CONSONANTS.map((char) => {
    const consonant = consonants.find((c) => c.char === char);
    if (!consonant) throw new Error(`語末子音が子音表に見つからない: ${char}`);
    return {
      id: `virama:${char}`,
      char: char + VIRAMA,
      romanized: consonantBase(consonant.romanized).normalize('NFC'),
      pairKey: char,
      note: 'ヴィラーマが内在母音 a を打ち消す',
    };
  });
}

/**
 * アヌスヴァーラ・ヴィサルガ（अयोगवाह）。
 *
 * 伝統文法は両者を ayogavāha =「常に他の音と組み合わせてのみ発音され、単独では
 * 決して発音されない」と定義する。したがって記号単体では出題せず、必ず音節に付けた形で出す。
 * 33 子音 × 2 = 66 件。バーラハカディー（क का कि की कु कू के कै को कौ कं कः）の 11・12 列目。
 *
 * 独立母音に付けた अं / अः は入れない。字母表の提示形としては伝統的だが、
 * コーパスでは ं の直前が独立母音なのは 0.1%、ः は 0.03% で、実テキストで出会う形ではない。
 */
const ANUSVARA = '\u0902';
const VISARGA = '\u0903';

export function generateAyogavaha(consonants: Character[] = characters.consonants): Character[] {
  const result: Character[] = [];

  for (const consonant of consonants) {
    result.push({
      id: `anusvara:${consonant.char}`,
      char: consonant.char + ANUSVARA,
      romanized: (consonant.romanized + '\u1E43').normalize('NFC'),
      pairKey: consonant.char,
      note: 'アヌスヴァーラを添えた形',
    });
    result.push({
      id: `visarga:${consonant.char}`,
      char: consonant.char + VISARGA,
      romanized: (consonant.romanized + '\u1E25').normalize('NFC'),
      pairKey: consonant.char,
      note: 'ヴィサルガを添えた形',
    });
  }

  return result;
}

/**
 * 結合子音（saṃyuktākṣara）。
 *
 * Whitney §15a は結合子音の全列挙を明確に否定している（実テキストに 473 種現れるので、
 * そもそも網羅にならない）。したがってここは「よく出会う形を通じて、組まれ方の規則に
 * 慣れる」ための標本であって、表ではない。選定はコーパス頻度を主、字形の型の網羅を従とした。
 *
 * 除外した規則が 3 つある。
 *
 * 1. **第 2 要素の ह を許さない。** IAST は有気音を 2 文字で書くので、क्ह = kha が
 *    既存の ख と、ग्ह = gha が घ と衝突する（ちょうど 10 件）。「ह は先頭要素にのみ許す」
 *    という一般形で書いてあるのは、将来 3 要素に広げたとき क्ह्क = khka が ख्क と
 *    衝突するのを同じ規則で防ぐため。verify-data.ts がこれを検査する。
 * 2. **3 子音以上を入れない。** 実テキストの全クラスタの 6.5% しかなく、しかも上位は
 *    「2 子音 + र」「2 子音 + य/व」で 2 子音の知識から導ける。誤答枠が 2 つしか無いので
 *    構成要素 3 つを同時に提示できないという実装上の理由もある。
 * 3. **ङ्क を入れない。** Devanagari Sangam MN と Kohinoor Devanagari で結合せず、
 *    可視ヴィラーマが残る（4 フォントで実測）。ヴィラーマ・カテゴリが教えた
 *    「可視ヴィラーマ＝母音が無い」と画面上で衝突する。ङ्ग は Kohinoor だけが落ちるので、
 *    tailwind.config.js のフォールバック順を Sangam 優先にして残した。
 */
export type ConjunctForm = 'ra-below' | 'repha' | 'ligature' | 'joined';

/**
 * 字形の型ごとの説明。
 *
 * 「半形」「縦積み」といった細かい分類はあえて持たない。実際にシェイピングして
 * 送り幅を測ると、त्त क्त न्न は半形ではなく単独グリフに融合し、द्य द्व द्ध も
 * 「下に積まれる」ではなく右下に融合する。手で付けた分類は字形と食い違いやすく、
 * 食い違った説明は説明が無いより悪い。ここに残したのは、文字そのものから機械的に
 * 判定できて反証されない 3 つ（第 2 要素が र / 第 1 要素が र / Whitney §13 が
 * 「構成要素の痕跡が認識できない」と名指しした 2 件）だけにしてある。
 */
const CONJUNCT_NOTES: Record<ConjunctForm, string> = {
  // どの型でも「まえの字が内在母音 a を失う」ことを必ず言う。これを言わないと、
  // 対比パネルの 3 行（正解 / 構成要素 र = ra / 構成要素 व = va）を足しても
  // ra + va = rava にしかならず、画面の情報だけでは正解 rva に到達できない。
  'ra-below':
    'まえの字が内在母音 a を失い、あとの ra が下に付く。まえの字の形も変わることがある',
  // 乗るのは र्（= r）であって र（= ra）ではない。このアプリは क = ka / क् = k と
  // 教えているので、ここで「ra が乗る」と書くと自分の用語法と矛盾する
  repha: 'まえの र が内在母音 a を失い、あとの字の上に鉤（レパ）として乗る',
  ligature: 'まえの字が内在母音 a を失う。構成要素の形が残らないので、この形ごと覚える',
  joined: '2 つの子音が 1 つの形に組まれる。まえの字は内在母音 a を失う',
};

/** 型は文字から導けるので、持つのはペアだけにする */
const CONJUNCT_PAIRS: [string, string][] = [
  // あとの र が下に付く形
  ['प', 'र'], ['त', 'र'], ['श', 'र'], ['क', 'र'], ['ब', 'र'], ['द', 'र'], ['ग', 'र'],
  // まえの र が上に乗る形（レパ）
  ['र', 'व'], ['र', 'म'], ['र', 'य'], ['र', 'त'], ['र', 'थ'], ['र', 'ग'], ['र', 'ष'],
  // 構成要素の痕跡が残らない合字
  ['क', 'ष'], ['ज', 'ञ'],
  // それ以外
  ['स', 'य'], ['न', 'त'], ['स', 'त'], ['त', 'य'], ['त', 'व'], ['त', 'त'],
  ['स', 'व'], ['क', 'त'], ['न', 'य'], ['व', 'य'], ['त', 'म'], ['स', 'म'],
  ['न', 'न'], ['स', 'थ'], ['च', 'छ'], ['ष', 'य'], ['न', 'द'], ['प', 'त'],
  ['स', 'क'], ['ध', 'य'], ['ष', 'ट'], ['द', 'ध'], ['द', 'य'], ['द', 'व'],
  ['ष', 'ठ'], ['ण', 'ड'], ['ञ', 'ज'], ['द', 'म'], ['ङ', 'ग'], ['ह', 'म'],
  ['श', 'च'], ['श', 'व'],
];

/** 構成要素の痕跡が残らないと Whitney §13 が名指しした 2 件 */
const LIGATURES = new Set(['क्ष', 'ज्ञ']);

export function conjunctForm(first: string, second: string): ConjunctForm {
  if (LIGATURES.has(first + VIRAMA + second)) return 'ligature';
  if (second === 'र') return 'ra-below';
  if (first === 'र') return 'repha';
  return 'joined';
}

/**
 * 構成要素の形が字に残らない結合子音か。
 *
 * Whitney §13 が「In a case or two, no trace of the constituent letters is
 * recognisable: thus, क्ष kṣa, ज्ञ jña」と名指しした 2 件。
 * この 2 件だけは「分解して読む」が通用しないので、対比パネルで構成要素を並べると
 * 画面に無いものを指して説明することになり、説明ではなく誤誘導になる。
 */
export function isOpaqueConjunct(char: string): boolean {
  return LIGATURES.has(char);
}

export function generateConjuncts(consonants: Character[] = characters.consonants): Character[] {
  return CONJUNCT_PAIRS.map(([a, b]) => {
    const first = consonants.find((c) => c.char === a);
    const second = consonants.find((c) => c.char === b);
    if (!first || !second) throw new Error(`結合子音の構成要素が子音表に見つからない: ${a}${b}`);
    if (b === 'ह') {
      // IAST の有気音 Ch と衝突する。先に落として、無言で誤った転写を作らせない
      throw new Error(`ह は結合子音の第 2 要素にできない（有気音の転写と衝突する）: ${a}${b}`);
    }

    const char = first.char + VIRAMA + second.char;
    return {
      id: `conjunct:${char}`,
      char,
      romanized: (consonantBase(first.romanized) + second.romanized).normalize('NFC'),
      pairKey: first.char,
      // 畳語（त्त / न्न）は重複を除く。除かないと対比パネルが同じ行を 2 度出す
      components: first.char === second.char ? [first.char] : [first.char, second.char],
      note: CONJUNCT_NOTES[conjunctForm(first.char, second.char)],
    };
  });
}

/** Fisher-Yates。元配列は破壊しない */
/**
 * Fisher–Yates。乱数源は省略時 Math.random。
 * 出題プールの引き（quiz-controller の #getAvailableCharacters / #seedOrder）には注入された乱数を
 * 渡し、テストを決定的にする。選択肢の生成（#generateOptions / #conjunctOptions）は既存の
 * 統計テストが Math.random の分布から閾値を導いているので、そのまま省略時の Math.random を使う
 */
export function shuffleArray<T>(array: T[], random: () => number = Math.random): T[] {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
}
